import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import ObtieneDatosCookies from "../obtieneDatosCookiesUsuario";
import { generarPDFDeclaracionJurada } from "../Inscripciones/generarPDFDeclaracionJurada";
import comprueboPermisos from "../tools/comprueboPermisos";
import CarpetaMedicaItem from "./CarpetaMedicaItem";
import EvaluacionMedicaItem from "./EvaluacionMedicaItem";
import UploadArchivosItem from '../Psicopedagogico/UploadAchivoItems'
import ModuloCompletoPerfeccionamiento from "./ModuloCompletoPerfeccionamiento";

const API = "https://wwwa.isepsantafe.net.ar/expedientes/ingresos";

const ListadoInscriptoSanidad = ({ etapa, presenteNuevo }) => {
  const Usuario = ObtieneDatosCookies();

  const puedeCompletarSanidad = comprueboPermisos(
    Usuario?.funcion,
    "Completo Procesos Sanidad"
  );

  const [paginaActual, setPaginaActual] = useState(1);
  const [ordenAlfabetico, setOrdenAlfabetico] = useState("apellido_asc");
  const [modalOpen, setModalOpen] = useState(false);
  const [itemSeleccionado, setItemSeleccionado] = useState(null);
  const [tipoModal, setTipoModal] = useState(null);
  const [data, setData] = useState([]);

  // Todos los presentes de todas las dependencias, agrupados por DNI.
  const [presentesPorDni, setPresentesPorDni] = useState({});

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Selección múltiple de evaluaciones
  const [evaluacionesSeleccionadas, setEvaluacionesSeleccionadas] = useState({});
  const [ocultarEvaluacionesCorregidas, setOcultarEvaluacionesCorregidas] =
    useState(false);
  const [mostrarPersonasSinDocumentacion, setMostrarPersonasSinDocumentacion] =
    useState(false);

  // Filtros operativos
  const [mostrarSoloPendientes, setMostrarSoloPendientes] = useState(false);
const [
  soloIncompletosSinIntervenir,
  setSoloIncompletosSinIntervenir
] = useState(false);
  // todos | presentes | sin_presente | sanidad
  const [filtroPresentes, setFiltroPresentes] = useState("todos");

  // Filtro de asistencia por fecha, igual al listado general.
  const [filtroPresenteFecha, setFiltroPresenteFecha] = useState(null);
  const [filtroTipoAsistencia, setFiltroTipoAsistencia] = useState("todos");

  const [colaEvaluaciones, setColaEvaluaciones] = useState([]);
  const [indiceEvaluacionCola, setIndiceEvaluacionCola] = useState(-1);
  const [guardandoCalificacionMasiva, setGuardandoCalificacionMasiva] =
    useState(false);

  const filasPorPagina = 100;

  const clavePaginaStorage = useMemo(() => {
    const procesoId = etapa?.proceso_id || "sin-proceso";
    const etapaId = etapa?.id || "sin-etapa";
    return `ListadoInscriptoSanidad:pagina:${procesoId}:${etapaId}`;
  }, [etapa?.proceso_id, etapa?.id]);

  const normalizar = (valor) =>
    String(valor || "")
      .toLowerCase()
      .trim();

  const esEvaluacion = (item) => normalizar(item?.tipo) === "evaluacion";

  // Para el filtro de pantalla consideramos "corregida y finalizada"
  // únicamente una evaluación aprobada/APTA.
  //
  // Las evaluaciones rechazadas, NO APTAS, pendientes o en progreso
  // deben seguir visibles para que Sanidad pueda revisarlas/modificarlas.
  const evaluacionTieneCorreccion = (item) => {
    if (!esEvaluacion(item)) return false;

    const estado = normalizar(item?.estado);
    const resultado = normalizar(item?.resultado).replaceAll("_", " ");

    const estadosFinalizadosAprobados = [
      "aprobado",
      "completado",
      "completo",
      "finalizado",
    ];

    const resultadosFinalizadosAprobados = [
      "aprobado",
      "completado",
      "completo",
      "apto",
    ];

    return (
      estadosFinalizadosAprobados.includes(estado) ||
      resultadosFinalizadosAprobados.includes(resultado)
    );
  };

  const getClaveEvaluacion = (personaProceso, instancia, item) =>
    [
      personaProceso?.persona?.id || personaProceso?.persona?.dni,
      instancia?.id,
      item?.persona_proceso_item_id ||
        item?.proceso_etapa_item_id ||
        item?.item_id ||
        item?.id,
    ].join("-");

  const esPerfeccionamiento = useMemo(() => {
    const textoEtapa = JSON.stringify(etapa || {}).toLowerCase();
    return textoEtapa.includes("perfeccionamiento");
  }, [etapa]);

  const itemEstaCompletadoOAprobado = (item) => {
    const estado = normalizar(item?.estado);
    const resultado = normalizar(item?.resultado);
    const formularioEstado = normalizar(item?.formulario_estado);
    const visible = normalizar(item?.visible);
    const tipo = normalizar(item?.tipo);

    if (
      tipo === "evaluacion" &&
      visible.includes("sanidad") &&
      !visible.includes("postulante")
    ) {
      return true;
    }

    return (
      estado === "aprobado" ||
      estado === "completado" ||
      estado === "completo" ||
      resultado === "aprobado" ||
      resultado === "completado" ||
      resultado === "completo" ||
      formularioEstado === "enviado"
    );
  };

const itemEntregadoOAprobado = (item) => {
  const estado = normalizar(item?.estado);
  const resultado = normalizar(item?.resultado);
  const formularioEstado = normalizar(item?.formulario_estado);

  return (
    estado === "aprobado" ||
    estado === "completado" ||
    estado === "completo" ||
    resultado === "aprobado" ||
    resultado === "completado" ||
    resultado === "completo" ||
    formularioEstado === "enviado"
  );
};

const itemEvaluacionInternaSanidad = (item) => {
  const visible = normalizar(item?.visible);
  const tipo = normalizar(item?.tipo);

  return (
    tipo === "evaluacion" &&
    visible.includes("sanidad") &&
    !visible.includes("postulante")
  );
};

const itemEsDocumentacionEntregada = (item) => {
  const tipo = normalizar(item?.tipo);
  const estado = normalizar(item?.estado);
  const resultado = normalizar(item?.resultado);
  const formularioEstado = normalizar(item?.formulario_estado);

  // La evaluación NO cuenta como documentación entregada.
  if (tipo === "evaluacion") return false;

  return (
    estado === "aprobado" ||
    estado === "completado" ||
    estado === "completo" ||
    resultado === "aprobado" ||
    resultado === "completado" ||
    resultado === "completo" ||
    formularioEstado === "enviado"
  );
};

const personaTieneDocumentacionEntregada = (personaProceso) =>
  Boolean(
    personaProceso?.instancias?.some((instancia) =>
      instancia?.items?.some((item) => itemEsDocumentacionEntregada(item))
    )
  );

const dataVisible = useMemo(() => {
  const personas = Array.isArray(data) ? data : [];

  // IMPORTANTE:
  // El filtro por documentación entregada era el que hacía que Sanidad
  // mostrara sólo una parte de los matriculados.
  //
  // Ese comportamiento solamente corresponde a Perfeccionamiento.
  // En el resto de los procesos mostramos TODAS las personas de la etapa.
  if (!esPerfeccionamiento) {
    return personas;
  }

  if (mostrarPersonasSinDocumentacion) {
    return personas;
  }

  return personas.filter((personaProceso) =>
    personaTieneDocumentacionEntregada(personaProceso)
  );
}, [
  data,
  esPerfeccionamiento,
  mostrarPersonasSinDocumentacion,
]);

const dataConFiltroCorrecciones = useMemo(() => {
  if (!ocultarEvaluacionesCorregidas) {
    return dataVisible;
  }

  // Al activar "Ocultar filas con evaluaciones ya realizadas",
  // ocultamos la FILA COMPLETA de la persona cuando ya no tiene
  // ninguna evaluación pendiente de revisión.
  //
  // Si todavía tiene al menos una evaluación pendiente, rechazada
  // o NO APTA, la fila permanece completa con todos sus documentos
  // y evaluaciones visibles.
  return dataVisible.filter((personaProceso) => {
    const tieneEvaluacionPendiente = personaProceso?.instancias?.some(
      (instancia) =>
        instancia?.items?.some(
          (item) =>
            esEvaluacion(item) && !evaluacionTieneCorreccion(item)
        )
    );

    return tieneEvaluacionPendiente;
  });
}, [dataVisible, ocultarEvaluacionesCorregidas]);

  const cantidadMatriculadosEtapa = Array.isArray(data) ? data.length : 0;

  const sinPermiso = () => {
    Swal.fire(
      "Sin permiso",
      "No tenés permiso para completar procesos de Sanidad",
      "warning"
    );
  };

const tieneIntervencion = (item) => {
  if (item?.intervenidoPorUsuario === true) return true;
  // Una DJ de evaluación con resultado guardado ya fue calificada,
  // aunque el registro no contenga una firma de auditoría en observaciones.
  if (normalizar(item?.tipo) === "formulario" &&
      normalizarObservacion(item?.observacion) === "evaluacion") {
    const resultado = normalizar(item?.resultado).replaceAll("_", " ");
    return resultado === "apto" || resultado === "no apto";
  }
  return false;
};

const etiquetaIntervencion = (item) => {
  const partes = ["Calificación registrada en Sanidad"];

  if (item?.usuarioIntervencion) {
    partes.push(`Usuario: ${item.usuarioIntervencion}`);
  }

  if (item?.fechaIntervencion) {
    const fecha = new Date(item.fechaIntervencion);

    if (!Number.isNaN(fecha.getTime())) {
      partes.push(
        `Fecha: ${fecha.toLocaleString("es-AR", {
          timeZone: "America/Argentina/Buenos_Aires",
        })}`
      );
    }
  }

  return partes.join(" | ");
};

const indicadorIntervencion = (item) => {
  if (!tieneIntervencion(item)) return null;

  return (
    <span
      title={etiquetaIntervencion(item)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        marginLeft: 6,
        padding: "2px 6px",
        borderRadius: 6,
        background: "#dbeafe",
        color: "#1d4ed8",
        border: "1px solid #93c5fd",
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      ✓ Calificado
    </span>
  );
};

  const getColor = (item) => {
    if (item.tipo === "formulario") {
      if (normalizarObservacion(item?.observacion) === "evaluacion") {
        const resultado = normalizar(item?.resultado).replaceAll("_", " ");
        if (resultado === "apto") return "#d1fae5";
        if (resultado === "no apto") return "#fee2e2";
        if (normalizar(item.formulario_estado) === "enviado") return "#fef9c3";
      }
      if (item.formulario_estado === "ENVIADO") return "#d1fae5";
      if (item.formulario_estado === "BORRADOR") return "#fef9c3";
      return "#fee2e2";
    }

    if (item.estado === "aprobado") return "#00e26d";
    if (item.estado === "cargado") return "#fef9c3";
    if (item.estado === "rechazado") return "#e43313";
    if (item.estado === "en_progreso") return "#fef9c3";
    return "#fee2e2";
  };

  const getIcon = (item) => {
    if (item.tipo === "formulario") {
      if (normalizarObservacion(item?.observacion) === "evaluacion") {
        const resultado = normalizar(item?.resultado).replaceAll("_", " ");
        if (resultado === "apto") return "✔";
        if (resultado === "no apto") return "✖";
        if (normalizar(item.formulario_estado) === "enviado") return "●";
      }
      if (item.formulario_estado === "ENVIADO") return "✔";
      if (item.formulario_estado === "BORRADOR") return "●";
      return "-";
    }

    if (item.estado === "aprobado") return "✔";
    if (item.estado === "rechazado") return "✖";
    if (item.estado === "en_progreso") return "●";
    if (item.estado === "cargado") return "●";
    return "-";
  };

  const aplicarGuardadosMasivosEnPantalla = (guardados = []) => {
    if (!Array.isArray(guardados) || guardados.length === 0) return;

    const mapaGuardados = new Map(
      guardados.map((guardado) => [
        `${String(guardado.persona_id)}-${String(guardado.item_id)}`,
        guardado,
      ])
    );

    setData((prev) =>
      prev.map((personaProceso) => {
        const personaId =
          personaProceso?.persona?.dni ||
          personaProceso?.persona_id ||
          personaProceso?.persona?.id;

        return {
          ...personaProceso,
          instancias:
            personaProceso.instancias?.map((instancia) => ({
              ...instancia,
              items:
                instancia.items?.map((item) => {
                  const itemId =
                    item?.proceso_etapa_item_id ||
                    item?.item_id ||
                    item?.id;

                  const guardado = mapaGuardados.get(
                    `${String(personaId)}-${String(itemId)}`
                  );

                  if (!guardado) return item;

                  return {
                    ...item,
                    estado: guardado.estado,
                    resultado: guardado.resultado,
                    intervenidoPorUsuario: true,
                    usuarioIntervencion:
                      guardado.usuario_nombre ||
                      guardado.usuarioNombre ||
                      `${Usuario?.apellido || ""} ${Usuario?.nombre || ""}`.trim() ||
                      String(Usuario?.identificacion || ""),
                    fechaIntervencion:
                      guardado.updatedAt || new Date().toISOString(),
                    persona_proceso_item_id:
                      guardado.persona_proceso_item_id ||
                      item.persona_proceso_item_id,
                  };
                }) || [],
            })) || [],
        };
      })
    );
  };

  // =====================================================
  // FORMATEO FECHA / HORA PRESENTES
  // =====================================================
  const formatearHoraPresente = (valor) => {
    if (!valor) return "";

    const texto = String(valor).trim();

    // Cuando viene desde MySQL TIME: "13:17:39"
    if (/^\d{2}:\d{2}:\d{2}$/.test(texto)) {
      return texto;
    }

    // Cuando el registro recién creado viene como ISO:
    // "2026-08-14T16:24:42.687Z"
    const fecha = new Date(texto);

    if (!Number.isNaN(fecha.getTime())) {
      return fecha.toLocaleTimeString("es-AR", {
        timeZone: "America/Argentina/Buenos_Aires",
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    }

    return texto;
  };

  const formatearFechaPresente = (valor) => {
    if (!valor) return "";

    const texto = String(valor).trim();

    // DATEONLY de MySQL: "2026-08-14"
    if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) {
      const [anio, mes, dia] = texto.split("-");
      return `${dia}/${mes}/${anio}`;
    }

    const fecha = new Date(texto);

    if (!Number.isNaN(fecha.getTime())) {
      return fecha.toLocaleDateString("es-AR", {
        timeZone: "America/Argentina/Buenos_Aires",
      });
    }

    return texto;
  };

  // =====================================================
  // PRESENTES DE TODAS LAS DEPENDENCIAS
  // =====================================================
  const getPresentesPersona = (personaProceso) => {
    const dni = String(
      personaProceso?.persona?.dni || ""
    ).trim();

    const presentes = presentesPorDni[dni];

    if (Array.isArray(presentes) && presentes.length > 0) {
      return presentes;
    }

    // Compatibilidad con respuestas que todavía traigan un único presente.
    if (personaProceso?.presente) {
      return [personaProceso.presente];
    }

    return [];
  };

  const getPresenteSanidad = (personaProceso) => {
    const presentes = getPresentesPersona(personaProceso);

    const presentesSanidad = presentes.filter(
      (presente) =>
        normalizar(presente?.dependencia) === "sanidad"
    );

    if (!presentesSanidad.length) {
      return null;
    }

    return [...presentesSanidad].sort((a, b) => {
      const valorA = `${a?.fecha || ""} ${a?.hora || ""}`;
      const valorB = `${b?.fecha || ""} ${b?.hora || ""}`;

      return valorB.localeCompare(valorA);
    })[0];
  };

  const getColorDependencia = (dependencia) => {
    const texto = normalizar(dependencia || "sin dependencia");

    const colores = [
      "#14532d",
      "#166534",
      "#15803d",
      "#16a34a",
      "#22c55e",
      "#4ade80",
      "#059669",
      "#047857",
      "#0f766e",
    ];

    let hash = 0;

    for (let i = 0; i < texto.length; i++) {
      hash = (hash * 31 + texto.charCodeAt(i)) >>> 0;
    }

    return colores[hash % colores.length];
  };

  const getColorTextoDependencia = (dependencia) => {
    const color = getColorDependencia(dependencia);

    // Los tonos más claros necesitan texto oscuro.
    return color === "#4ade80" || color === "#22c55e"
      ? "#052e16"
      : "#ffffff";
  };

  // =====================================================
  // AGREGAR PRESENTE NUEVO SIN VOLVER A CONSULTAR
  // =====================================================
  const agregarPresenteLocal = (registro) => {
    if (!registro) return;

    const dni = String(registro?.dni || "").trim();

    if (!dni) return;

    const etapaRegistro = String(
      registro?.proceso_etapa_id || ""
    );

    const etapaActual = String(
      etapa?.id || ""
    );

    if (
      etapaRegistro &&
      etapaActual &&
      etapaRegistro !== etapaActual
    ) {
      return;
    }

    setPresentesPorDni((prev) => {
      const actuales = Array.isArray(prev[dni])
        ? prev[dni]
        : [];

      const yaExiste = actuales.some((presente) => {
        if (registro?.id && presente?.id) {
          return String(registro.id) === String(presente.id);
        }

        return (
          String(presente?.dependencia || "").trim() ===
            String(registro?.dependencia || "").trim() &&
          String(presente?.fecha || "").trim() ===
            String(registro?.fecha || "").trim() &&
          String(presente?.hora || "").trim() ===
            String(registro?.hora || "").trim()
        );
      });

      if (yaExiste) {
        return prev;
      }

      const nuevos = [
        ...actuales,
        {
          ...registro,
          dni,
          proceso_etapa_id:
            registro?.proceso_etapa_id ||
            etapa?.id,
        },
      ].sort((a, b) => {
        const valorA = `${a?.fecha || ""} ${a?.hora || ""}`;
        const valorB = `${b?.fecha || ""} ${b?.hora || ""}`;

        return valorA.localeCompare(valorB);
      });

      return {
        ...prev,
        [dni]: nuevos,
      };
    });
  };

  const fetchPresentes = async () => {
    try {
      if (!etapa?.id || esPerfeccionamiento) {
        setPresentesPorDni({});
        return;
      }

      // Sin dependencia: trae Sanidad, Psicología, Inscripción,
      // Especialidades, Procesos, etc.
      const res = await axios.post(
        `${API}/listarPresentesEtapa`,
        {
          proceso_etapa_id: etapa.id,
        }
      );

      const lista = Array.isArray(res.data?.presentes)
        ? res.data.presentes
        : [];

      const mapa = {};

      lista.forEach((presente) => {
        const dni = String(presente?.dni || "").trim();

        if (!dni) return;

        if (!mapa[dni]) {
          mapa[dni] = [];
        }

        mapa[dni].push(presente);
      });

      Object.keys(mapa).forEach((dni) => {
        mapa[dni] = mapa[dni].sort((a, b) => {
          const valorA = `${a?.fecha || ""} ${a?.hora || ""}`;
          const valorB = `${b?.fecha || ""} ${b?.hora || ""}`;

          return valorA.localeCompare(valorB);
        });
      });

      setPresentesPorDni(mapa);
    } catch (error) {
      console.error(
        "Error cargando presentes de todas las dependencias:",
        error
      );

      setPresentesPorDni({});
    }
  };

  const mostrarPresentes = (presentes = []) => {
    if (!Array.isArray(presentes) || presentes.length === 0) {
      return;
    }

    const html = presentes
      .map((presente) => {
        const dependencia =
          presente?.dependencia || "Sin dependencia";

        const color = getColorDependencia(dependencia);
        const colorTexto = getColorTextoDependencia(dependencia);

        return `
          <div
            style="
              text-align:left;
              padding:12px;
              margin-bottom:10px;
              border-radius:10px;
              background:${color};
              color:${colorTexto};
            "
          >
            <div style="font-weight:800;margin-bottom:6px">
              ✔ ${dependencia}
            </div>

            <div>
              <b>Fecha:</b>
              ${formatearFechaPresente(presente?.fecha) || "—"}
            </div>

            <div>
              <b>Hora:</b>
              ${formatearHoraPresente(presente?.hora) || "—"}
            </div>

            <div>
              <b>Usuario:</b>
              ${presente?.usuario || "—"}
            </div>
          </div>
        `;
      })
      .join("");

    Swal.fire({
      title:
        presentes.length === 1
          ? "Presente registrado"
          : `Presentes registrados (${presentes.length})`,
      html: `
        <div style="max-height:450px;overflow:auto;padding-right:4px">
          ${html}
        </div>
      `,
      icon: "info",
      width: 700,
      confirmButtonText: "Cerrar",
    });
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      // Mismo endpoint que usa el listado general:
      // de esta forma recibimos todas las personas matriculadas en la etapa.
      const res = await axios.post(`${API}/getPersonasPorEtapa`, {
        proceso_etapa_id: etapa.id,
      });

      const personas = Array.isArray(res.data?.personas)
        ? res.data.personas
        : [];
console.table(
  personas.flatMap((p) =>
    (p.instancias || []).flatMap((inst) =>
      (inst.items || []).map((item) => ({
        dni: p.persona?.dni,
        item: item.descripcion || item.nombre,
        estado: item.estado,
        registro: item.persona_proceso_item_id,
        intervenido: item.intervenidoPorUsuario,
        usuario: item.usuarioIntervencion,
        fecha: item.fechaIntervencion,
      }))
    )
  )
);
      // Conservamos todas las personas, pero dentro de cada instancia
      // mostramos solamente los ítems que corresponden a Sanidad
      // o ítems generales sin restricción de visibilidad.
      const personasSanidad = personas.map((personaProceso) => ({
        ...personaProceso,
        instancias:
          personaProceso?.instancias?.map((instancia) => ({
            ...instancia,
            items:
              instancia?.items?.filter((item) => {
                const visible = normalizar(item?.visible);

                return (
                  !visible ||
                  visible.includes("sanidad")
                );
              }) || [],
          })) || [],
      }));

      setData(personasSanidad);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "No se pudo cargar Sanidad", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!etapa?.id) return;

    const paginaGuardada = Number(localStorage.getItem(clavePaginaStorage));

    setPaginaActual(
      Number.isInteger(paginaGuardada) && paginaGuardada > 0
        ? paginaGuardada
        : 1
    );

    fetchData();
    fetchPresentes();
  }, [etapa, clavePaginaStorage]);

  // Cuando el lector crea un presente, ProcesosSanidadViewer
  // nos pasa el registro devuelto por el backend.
  // Lo incorporamos directamente al mapa local:
  // NO hacemos fetchPresentes() nuevamente.
  useEffect(() => {
    if (!presenteNuevo) return;

    agregarPresenteLocal(
      presenteNuevo
    );
  }, [presenteNuevo, etapa?.id]);

  useEffect(() => {
    setPaginaActual(1);
  }, [
    search,
    filtroPresentes,
    filtroPresenteFecha,
    filtroTipoAsistencia,
    mostrarSoloPendientes,
    soloIncompletosSinIntervenir,
  ]);

  useEffect(() => {
    if (!etapa?.id) return;

    localStorage.setItem(
      clavePaginaStorage,
      String(paginaActual)
    );
  }, [paginaActual, clavePaginaStorage, etapa?.id]);

  const resumenPresentes = useMemo(() => {
    const porFecha = {};
    const porDependencia = {};

    let totalPersonasConPresente = 0;
    let totalRegistros = 0;

    (Array.isArray(data) ? data : []).forEach((personaProceso) => {
      const presentes = getPresentesPersona(personaProceso);

      if (presentes.length > 0) {
        totalPersonasConPresente++;
      }

      // Una persona cuenta una sola vez por fecha aunque tenga
      // varios presentes de distintas dependencias ese mismo día.
      const fechasPersona = new Set();

      presentes.forEach((presente) => {
        const fecha = String(presente?.fecha || "").trim();

        if (fecha) {
          fechasPersona.add(fecha);
        }

        const dependencia = String(
          presente?.dependencia || "Sin dependencia"
        ).trim();

        if (!porDependencia[dependencia]) {
          porDependencia[dependencia] = 0;
        }

        porDependencia[dependencia]++;
        totalRegistros++;
      });

      fechasPersona.forEach((fecha) => {
        if (!porFecha[fecha]) {
          porFecha[fecha] = 0;
        }

        porFecha[fecha]++;
      });
    });

    return {
      porFecha,
      porDependencia,
      totalPersonasConPresente,
      totalRegistros,
    };
  }, [data, presentesPorDni]);

  const filteredData = useMemo(() => {
    return dataConFiltroCorrecciones.filter((personaProceso) => {
      const persona = personaProceso?.persona || {};

      // =====================================================
      // BUSCADOR
      // =====================================================
      const texto = `${persona.dni || ""} ${persona.nombre || ""} ${
        persona.apellido || ""
      }`.toLowerCase();

      if (!texto.includes(search.toLowerCase())) {
        return false;
      }

      // =====================================================
      // FILTRO DE PRESENTES
      // =====================================================
      const presentes = getPresentesPersona(personaProceso);
      const tieneAlgunPresente = presentes.length > 0;
      const tienePresenteSanidad = Boolean(
        getPresenteSanidad(personaProceso)
      );

      // Filtro general.
      if (
        filtroPresentes === "presentes" &&
        !tieneAlgunPresente
      ) {
        return false;
      }

      if (
        filtroPresentes === "sin_presente" &&
        tieneAlgunPresente
      ) {
        return false;
      }

      if (
        filtroPresentes === "sanidad" &&
        !tienePresenteSanidad
      ) {
        return false;
      }

      // Filtro por una fecha determinada.
      if (filtroPresenteFecha) {
        const tienePresenteEnFecha = presentes.some(
          (presente) =>
            String(presente?.fecha || "") ===
            String(filtroPresenteFecha)
        );

        if (
          filtroTipoAsistencia === "presentes" &&
          !tienePresenteEnFecha
        ) {
          return false;
        }

        if (
          filtroTipoAsistencia === "restantes" &&
          tienePresenteEnFecha
        ) {
          return false;
        }
      }

      // Ítems incompletos que todavía no tienen intervención registrada.
      if (soloIncompletosSinIntervenir) {
        const tieneSinRevisar = personaProceso?.instancias?.some(
          (instancia) => instancia?.items?.some(
            (item) => !itemEstaCompletadoOAprobado(item) && !tieneIntervencion(item)
          )
        );
        if (!tieneSinRevisar) return false;
      }

      // =====================================================
      // FILTRO: SOLO PERSONAS CON COSAS PENDIENTES
      // =====================================================
      if (mostrarSoloPendientes) {
        const tienePendientes = Boolean(
          personaProceso?.instancias?.some((instancia) =>
            instancia?.items?.some(
              (item) => !itemEstaCompletadoOAprobado(item)
            )
          )
        );

        if (!tienePendientes) {
          return false;
        }
      }

      return true;
    });
  }, [
    dataConFiltroCorrecciones,
    search,
    mostrarSoloPendientes,
    soloIncompletosSinIntervenir,
    filtroPresentes,
    filtroPresenteFecha,
    filtroTipoAsistencia,
    presentesPorDni,
  ]);

  const dataOrdenada = useMemo(() => {
    const lista = [...filteredData];

    const compararTexto = (a, b) =>
      String(a || "").localeCompare(String(b || ""), "es", {
        sensitivity: "base",
        numeric: true,
      });

    lista.sort((filaA, filaB) => {
      const personaA = filaA?.persona || {};
      const personaB = filaB?.persona || {};

      if (ordenAlfabetico === "apellido_desc") {
        return (
          compararTexto(personaB.apellido, personaA.apellido) ||
          compararTexto(personaB.nombre, personaA.nombre)
        );
      }

      if (ordenAlfabetico === "nombre_asc") {
        return (
          compararTexto(personaA.nombre, personaB.nombre) ||
          compararTexto(personaA.apellido, personaB.apellido)
        );
      }

      if (ordenAlfabetico === "nombre_desc") {
        return (
          compararTexto(personaB.nombre, personaA.nombre) ||
          compararTexto(personaB.apellido, personaA.apellido)
        );
      }

      return (
        compararTexto(personaA.apellido, personaB.apellido) ||
        compararTexto(personaA.nombre, personaB.nombre)
      );
    });

    return lista;
  }, [filteredData, ordenAlfabetico]);

  const totalPaginas =
    Math.ceil(dataOrdenada.length / filasPorPagina) || 1;

  useEffect(() => {
    if (paginaActual > totalPaginas) {
      setPaginaActual(totalPaginas);
    }
  }, [paginaActual, totalPaginas]);

  const dataPaginada = useMemo(() => {
    const inicio = (paginaActual - 1) * filasPorPagina;
    const fin = inicio + filasPorPagina;
    return dataOrdenada.slice(inicio, fin);
  }, [dataOrdenada, paginaActual]);

  const resumenPorInstancia = useMemo(() => {
    const resumen = {};

    filteredData.forEach((p) => {
      p.instancias?.forEach((inst) => {
        if (!resumen[inst.id]) {
          resumen[inst.id] = {
            aprobado: 0,
            pendiente: 0,
            en_progreso: 0,
            nombre: inst.nombre,
          };
        }

        inst.items?.forEach((item) => {
          if (item.tipo === "formulario") {
            if (item.formulario_estado === "ENVIADO") {
              resumen[inst.id].aprobado++;
            } else if (item.formulario_estado === "BORRADOR") {
              resumen[inst.id].en_progreso++;
            } else {
              resumen[inst.id].pendiente++;
            }
          } else {
            if (item.estado === "aprobado") {
              resumen[inst.id].aprobado++;
            } else if (
              item.estado === "en_progreso" ||
              item.estado === "cargado"
            ) {
              resumen[inst.id].en_progreso++;
            } else {
              resumen[inst.id].pendiente++;
            }
          }
        });
      });
    });

    return resumen;
  }, [filteredData]);

 
  // "observacion" identifica la clase de formulario configurada.
  // "observaciones" contiene la calificación individual del postulante.
  const esFormularioEvaluacion = (item) =>
    normalizar(item?.tipo) === "formulario" &&
    normalizarObservacion(item?.observacion) === "evaluacion";

  const resultadoFormularioEvaluacion = (item) => {
    const valor = normalizarObservacion(item?.resultado).replaceAll("_", " ");
    if (valor === "apto") return "APTO";
    if (valor === "no apto") return "NO APTO";
    return null;
  };

  const escaparHtml = (valor) =>
    String(valor ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const abrirFormulario = async (item, persona = null, instancia = null) => {
    const requiereEvaluacion = esFormularioEvaluacion(item);

    const generarDocumento = async (calificacion = null) => {
      console.log("Generando PDF de declaración jurada para", {
        dni: item?.dni || item?.persona_dni || persona?.dni,
        persona_id: item?.persona_id || persona?.id,
        formulario_id: item?.formulario_id,
      });
      const respuesta = await axios.post(`${API}/obtenerDeclaracionJurada`, {
        dni: item?.dni || item?.persona_dni || persona?.dni,
        persona_id: item?.persona_id || persona?.id,
        formulario_id: item?.formulario_id,
      });
      // La vista previa no exige calificación: primero Sanidad debe poder
      // revisar el formulario. El PDF calificado se genera sólo cuando
      // existe un resultado registrado.
      return generarPDFDeclaracionJurada(respuesta.data, {
        evaluacionFormulario: requiereEvaluacion && Boolean(calificacion?.resultado),
        calificacion,
        texto_aprobado: item?.texto_aprobado,
        texto_rechazado: item?.texto_rechazado,
      });
    };

    try {
      if (!requiereEvaluacion) {
        await generarDocumento();
        return;
      }
      if (!puedeCompletarSanidad) {
        sinPermiso();
        return;
      }

      const resultadoActual = resultadoFormularioEvaluacion(item);
      const observacionesActuales = String(item?.observaciones ?? "");
      const enviado = normalizar(item?.formulario_estado) === "enviado";

      if (!enviado) {
        await Swal.fire(
          "Formulario pendiente",
          "El postulante todavía no envió este formulario.",
          "info"
        );
        return;
      }

      const respuesta = await Swal.fire({
        title: "Evaluación de Sanidad",
        width: 620,
        html: `
          <div style="text-align:left">
            <p><b>${escaparHtml(item?.nombre || item?.descripcion || "Formulario")}</b></p>
            <p>Postulante: <b>${escaparHtml(persona?.apellido)}, ${escaparHtml(persona?.nombre)}</b></p>
            <p>DNI: <b>${escaparHtml(persona?.dni || "-")}</b></p>
            <hr/>
            <label style="display:block;font-weight:700;margin-bottom:6px">Calificación</label>
            <select id="sanidad-resultado" class="swal2-select" style="width:100%;margin:0 0 12px 0">
              <option value="">Seleccionar...</option>
              <option value="APTO" ${resultadoActual === "APTO" ? "selected" : ""}>APTO</option>
              <option value="NO APTO" ${resultadoActual === "NO APTO" ? "selected" : ""}>NO APTO</option>
            </select>
            <label style="display:block;font-weight:700;margin-bottom:6px">Observaciones</label>
            <textarea id="sanidad-observaciones" class="swal2-textarea"
              style="width:100%;margin:0;min-height:100px"
              placeholder="Motivos u observaciones">${escaparHtml(observacionesActuales)}</textarea>
            <p style="font-size:12px;margin-top:12px">
              ${resultadoActual ? `Calificación guardada: <b>${resultadoActual}</b>` : "Sin calificación registrada"}
            </p>
          </div>`,
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: "Guardar calificación",
        denyButtonText: "📄 Ver Declaración Jurada",
        cancelButtonText: "Cerrar",
        confirmButtonColor: "#2563eb",
        denyButtonColor: "#475569",
        preConfirm: () => {
          const resultado = document.getElementById("sanidad-resultado")?.value;
          const observaciones = document.getElementById("sanidad-observaciones")?.value?.trim() || "";
          if (!resultado) {
            Swal.showValidationMessage("Seleccioná APTO o NO APTO.");
            return false;
          }
          if (resultado === "NO APTO" && !observaciones) {
            Swal.showValidationMessage("Indicá los motivos de NO APTO.");
            return false;
          }
          return { resultado, observaciones };
        },
      });

      if (respuesta.isDismissed) return;

      if (respuesta.isDenied) {
        await generarDocumento(
          resultadoActual
            ? {
                resultado: resultadoActual,
                observacion: observacionesActuales,
              }
            : null
        );
        return;
      }

      if (!respuesta.isConfirmed || !respuesta.value) return;
      const { resultado, observaciones } = respuesta.value;
      const personaId = persona?.dni || item?.dni || item?.persona_dni;
      const itemId = item?.proceso_etapa_item_id || item?.item_id || item?.id;

      if (!personaId || !itemId) {
        throw new Error("Falta DNI o identificador del ítem para calificar.");
      }

      const payload = {
        persona_id: personaId,
        item_id: itemId,
        tipo: "formulario",
        estado: resultado === "APTO" ? "aprobado" : "rechazado",
        resultado,
        observaciones,
        proceso_id: etapa?.proceso_id,
        usuario_id: Usuario?.id || Usuario?.usuarioID || Usuario?.identificacion || null,
        usuario_nombre: `${Usuario?.identificacion || ""} ${Usuario?.apellido || ""}, ${Usuario?.nombre || ""}`.trim(),
      };

      // Endpoint individual. NO usar togglePersonaItemMasivo aquí.
      const guardado = await axios.post(`${API}/togglePersonaItem`, payload);
      if (guardado.data?.ok !== true || !guardado.data?.data) {
        throw new Error("El servidor no confirmó el guardado de la calificación.");
      }

      // Refrescar desde backend y actualizar también la fila local
      // para no depender de que getPersonasPorEtapa incluya observaciones.
      await fetchData();
      setData((prev) => prev.map((p) => ({
        ...p,
        instancias: (p.instancias || []).map((inst) => ({
          ...inst,
          items: (inst.items || []).map((i) => {
            const mismoDni = String(p.persona?.dni) === String(personaId);
            const mismoItem = String(i.proceso_etapa_item_id || i.item_id || i.id) === String(itemId);
            return mismoDni && mismoItem
              ? { ...i, estado: payload.estado, resultado, observaciones, intervenidoPorUsuario: true }
              : i;
          }),
        })),
      })));

      await Swal.fire({
        icon: "success",
        title: "Calificación guardada",
        text: `${resultado} registrado. Volvé a abrir el formulario para ver el PDF firmado.`,
      });
      // Nunca abrir PDF automáticamente al guardar.
    } catch (error) {
      console.error("Error al calificar formulario:", error);
      await Swal.fire(
        "Error",
        error?.response?.data?.detalle || error?.response?.data?.error ||
          error?.message || "No se pudo completar la operación.",
        "error"
      );
    }
  };

  const abrirModalArchivoNormal = (persona, instancia, item) => {
    setItemSeleccionado({ persona, instancia, item });
    setTipoModal("archivo");
    setModalOpen(true);
  };

  const abrirArchivoSoloLectura = (persona, instancia, item) => {
    setItemSeleccionado({
      persona,
      instancia,
      item,
      persona_id: persona?.id,
      persona_dni: persona?.dni,
      dni: persona?.dni,
      item_id: item?.id,
      proceso_etapa_item_id: item?.id,
      proceso_id: etapa?.proceso_id,
      proceso_etapa_id: etapa?.id,
      instancia_id: instancia,
      idPersona: persona?.id,
      idItem: item?.id,
      soloLectura: true,
      puedeEliminar: false,
      puedeCargar: false,
      esPerfeccionamiento: true,
    });

    setTipoModal("archivo_solo_lectura");
    setModalOpen(true);
  };

  const abrirModalEvaluacionMedica = (persona, instancia, item) => {
    setItemSeleccionado({ persona, instancia, item });
    setTipoModal("evaluacion_medica");
    setModalOpen(true);
  };

  const abrirModalCarpetaMedica = (persona, instancia, item) => {
    setItemSeleccionado({ persona, instancia, item });
    setTipoModal("carpeta_medica");
    setModalOpen(true);
  };

  const esEntregaCarpetaMedica = (item) => {
    return (
      item?.tipo === "archivo" &&
      item?.descripcion?.toLowerCase().includes("entrega carpeta medica")
    );
  };

  const abroExamenSanidadPreocupacional = (persona, instancia, itemId,procesoId) => {
    localStorage.setItem("dniFormulario", persona.dni.toString());
    localStorage.setItem(
      "apellidoNombre",
      `${persona.apellido} ${persona.nombre}`
    );
    localStorage.setItem(
      "usuarioGuardo",
      Usuario?.identificacion?.toString() || ""
    );
    localStorage.setItem("etapa", instancia.toString());
    localStorage.setItem("proceso", String(procesoId ?? ""));
    if (itemId == null) {
      console.error("Preocupacional sin itemId", { persona, instancia, procesoId });
      Swal.fire("Error", "El ítem preocupacional no tiene un ID válido", "error");
      return;
    }
    localStorage.setItem("item", String(itemId));

    const ventana = window.open("/SanidadFormulario", "_blank");
    if (!ventana) {
      Swal.fire({
        icon: "warning",
        title: "El navegador bloqueó la ventana",
        text: "Permití las ventanas emergentes para abrir el formulario preocupacional.",
      });
    }
  };

  const abroExamenSanidadPerfeccionamiento = (persona, instancia, item) => {
    const itemSeguro = {
      ...(item || {}),
      id: item?.id || item?.item_id || item?.proceso_etapa_item_id || null,
      item_id: item?.id || item?.item_id || item?.proceso_etapa_item_id || null,
      proceso_etapa_item_id:
        item?.id || item?.item_id || item?.proceso_etapa_item_id || null,
      persona_proceso_item_id: item?.persona_proceso_item_id || null,
      tipo: item?.tipo || "evaluacion",
      observacion: item?.observacion || "Perfeccionamiento",
      nombre: item?.nombre || "Evaluación Perfeccionamiento",
      visible: item?.visible || "Sanidad",
      proceso_etapa_instancia_id:
        item?.proceso_etapa_instancia_id || instancia,
      debeCrearItem:
        !item?.id && !item?.item_id && !item?.proceso_etapa_item_id,
    };

    setItemSeleccionado({
      persona,
      instancia,
      item: itemSeguro,
      itemId: itemSeguro.id,
      item_id: itemSeguro.id,
      proceso_etapa_item_id: itemSeguro.proceso_etapa_item_id,
      persona_proceso_item_id: itemSeguro.persona_proceso_item_id,
      persona_id: persona?.id,
      persona_dni: persona?.dni,
      dni: persona?.dni,
      proceso_id: etapa?.proceso_id,
      proceso_etapa_id: etapa?.id,
      instancia_id: instancia,
      debeCrearItem: itemSeguro.debeCrearItem,
    });

    setTipoModal("completoPerfeccionamiento");
    setModalOpen(true);
  };

  const normalizarObservacion = (valor) =>
    String(valor ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

  // Reconocer Preocupacional por los metadatos del ítem, sin depender
  // exclusivamente de observacion ni de mayúsculas o acentos.
  const esPreocupacional = (item) => {
    const campos = [
      item?.observacion,
      item?.descripcion,
      item?.nombre,
      item?.titulo,
      item?.tipo_evaluacion,
      item?.tipoEvaluacion,
    ];
    if (normalizarObservacion(item?.tipo) !== "evaluacion") return false;
    return campos.some((campo) => {
      const texto = normalizarObservacion(campo);
      return texto.includes("preocupacional") || /\bpre[\s_-]*medico\b/.test(texto);
    });
  };

  const handleClickItem = (p, inst, item) => {
    if (!puedeCompletarSanidad) {
      sinPermiso();
      return;
    }

    // Preocupacional debe evaluarse ANTES que las rutas genéricas.
    if (esPreocupacional(item)) {
      abroExamenSanidadPreocupacional(
        p.persona,
        inst.id,
        item.id ?? item.item_id ?? item.proceso_etapa_item_id,
        etapa?.proceso_id
      );
      return;
    }

    if (normalizar(item?.tipo) === "archivo") {
      if (esPerfeccionamiento) {
        abrirArchivoSoloLectura(p.persona, inst.id, item);
        return;
      }

      if (esEntregaCarpetaMedica(item)) {
        abrirModalCarpetaMedica(p.persona, inst.id, item);
        return;
      }

      abrirModalArchivoNormal(p.persona, inst.id, item);
      return;
    }

    if (
      normalizar(item?.tipo) === "evaluacion" &&
      normalizarObservacion(item?.observacion) === "carpeta medica"
    ) {
      abrirModalEvaluacionMedica(p.persona, inst.id, item);
      return;
    }

    if (normalizar(item?.tipo) === "evaluacion" && esPerfeccionamiento) {
      abroExamenSanidadPerfeccionamiento(p.persona, inst.id, item);
      return;
    }

    if (normalizar(item?.tipo) === "formulario") {
      abrirFormulario({
        ...item,
        dni: p.persona.dni,
        persona_id: p.persona.id,
      }, p.persona, inst);
      return;
    }

    // Evaluaciones generales de Sanidad (por ejemplo, "Resultado").
    // Las rutas específicas anteriores conservan su prioridad.
    if (normalizar(item?.tipo) === "evaluacion") {
      abrirModalEvaluacionMedica(p.persona, inst.id, item);
      return;
    }

    console.warn("Ítem de Sanidad sin ruta de apertura:", item);
  };

  const evaluacionesVisibles = useMemo(() => {
    const lista = [];

    filteredData.forEach((personaProceso) => {
      personaProceso.instancias?.forEach((instancia) => {
        instancia.items?.forEach((item) => {
          if (esEvaluacion(item)) {
            const clave = getClaveEvaluacion(
              personaProceso,
              instancia,
              item
            );

            lista.push({
              clave,
              personaProceso,
              instancia,
              item,
            });
          }
        });
      });
    });

    return lista;
  }, [filteredData]);

  const cantidadSeleccionadas = Object.keys(
    evaluacionesSeleccionadas
  ).length;

  const todasLasEvaluacionesVisiblesSeleccionadas =
    evaluacionesVisibles.length > 0 &&
    evaluacionesVisibles.every(
      ({ clave }) => evaluacionesSeleccionadas[clave]
    );

  const toggleEvaluacionSeleccionada = (
    personaProceso,
    instancia,
    item
  ) => {
    if (!puedeCompletarSanidad) {
      sinPermiso();
      return;
    }

    const clave = getClaveEvaluacion(personaProceso, instancia, item);

    setEvaluacionesSeleccionadas((prev) => {
      const siguiente = { ...prev };

      if (siguiente[clave]) {
        delete siguiente[clave];
      } else {
        siguiente[clave] = {
          clave,
          personaProceso,
          instancia,
          item,
        };
      }

      return siguiente;
    });
  };

  const toggleSeleccionarTodasEvaluaciones = () => {
    if (!puedeCompletarSanidad) {
      sinPermiso();
      return;
    }

    setEvaluacionesSeleccionadas((prev) => {
      const siguiente = { ...prev };

      if (todasLasEvaluacionesVisiblesSeleccionadas) {
        evaluacionesVisibles.forEach(({ clave }) => {
          delete siguiente[clave];
        });
      } else {
        evaluacionesVisibles.forEach((registro) => {
          siguiente[registro.clave] = registro;
        });
      }

      return siguiente;
    });
  };

  const limpiarSeleccionEvaluaciones = () => {
    setEvaluacionesSeleccionadas({});
    setColaEvaluaciones([]);
    setIndiceEvaluacionCola(-1);
  };

  const abrirEvaluacionDeCola = (indice, cola = colaEvaluaciones) => {
    const registro = cola[indice];
    if (!registro) return;

    setIndiceEvaluacionCola(indice);

    handleClickItem(
      registro.personaProceso,
      registro.instancia,
      registro.item
    );
  };

  const calificarSeleccionadasMasivamente = async () => {
    if (!puedeCompletarSanidad) {
      sinPermiso();
      return;
    }

    const seleccionadas = Object.values(evaluacionesSeleccionadas);

    if (!seleccionadas.length) {
      Swal.fire(
        "Sin evaluaciones",
        "Seleccioná al menos una evaluación.",
        "info"
      );
      return;
    }

    const { value: formulario } = await Swal.fire({
      title: `Calificar ${seleccionadas.length} evaluación(es)`,
      html: `
        <div style="text-align:left">
          <label style="display:block;font-weight:bold;margin-bottom:6px">
            Resultado
          </label>

          <select
            id="swal-resultado-masivo"
            class="swal2-select"
            style="width:100%;margin:0 0 14px 0"
          >
            <option value="">Seleccione...</option>
            <option value="apto">APTO</option>
            <option value="no_apto">NO APTO</option>
          </select>

          <div
            id="contenedor-motivos-no-apto"
            style="display:none;margin-bottom:14px"
          >
            <label style="display:block;font-weight:bold;margin-bottom:6px">
              Motivos de NO APTO
            </label>

            <textarea
              id="swal-motivos-no-apto"
              class="swal2-textarea"
              placeholder="Ingresá los motivos de NO APTO"
              style="width:100%;margin:0;min-height:90px"
            ></textarea>
          </div>

          <label style="display:block;font-weight:bold;margin-bottom:6px">
            Observación común
          </label>

          <textarea
            id="swal-observacion-masiva"
            class="swal2-textarea"
            placeholder="Observación adicional"
            style="width:100%;margin:0;min-height:110px"
          ></textarea>

          <div
            style="
              margin-top:12px;
              padding:9px;
              background:#eff6ff;
              border-radius:7px;
              font-size:13px;
            "
          >
            Se creará o actualizará la evaluación de las personas seleccionadas
            usando el endpoint masivo.
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Continuar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#2563eb",
      focusConfirm: false,
      didOpen: () => {
        const selectResultado = document.getElementById(
          "swal-resultado-masivo"
        );

        const contenedorMotivos = document.getElementById(
          "contenedor-motivos-no-apto"
        );

        const actualizarVisibilidadMotivos = () => {
          const esNoApto = selectResultado?.value === "no_apto";

          if (contenedorMotivos) {
            contenedorMotivos.style.display = esNoApto
              ? "block"
              : "none";
          }

          if (!esNoApto) {
            const inputMotivos = document.getElementById(
              "swal-motivos-no-apto"
            );

            if (inputMotivos) {
              inputMotivos.value = "";
            }
          }
        };

        selectResultado?.addEventListener(
          "change",
          actualizarVisibilidadMotivos
        );

        actualizarVisibilidadMotivos();
      },
      preConfirm: () => {
        const resultado = document.getElementById(
          "swal-resultado-masivo"
        )?.value;

        const observacion = document
          .getElementById("swal-observacion-masiva")
          ?.value?.trim();

        const motivosNoApto = document
          .getElementById("swal-motivos-no-apto")
          ?.value?.trim();

        if (!resultado) {
          Swal.showValidationMessage("Seleccioná APTO o NO APTO.");
          return false;
        }

        if (resultado === "no_apto" && !motivosNoApto) {
          Swal.showValidationMessage(
            "Para marcar NO APTO debés ingresar los motivos."
          );
          return false;
        }

        return {
          resultado,
          observacion: observacion || "",
          motivos:
            resultado === "no_apto" && motivosNoApto
              ? [motivosNoApto]
              : [],
        };
      },
    });

    if (!formulario) return;

    const estado =
      formulario.resultado === "apto" ? "aprobado" : "rechazado";

    const textoResultado =
      formulario.resultado === "apto" ? "APTO" : "NO APTO";

    const evaluacionesPayload = seleccionadas
      .map(({ personaProceso, instancia, item }) => {
        const persona = personaProceso?.persona || {};

        // En este sistema persona_id se guarda con el DNI.
        const personaId =
          persona?.dni ||
          personaProceso?.persona_id ||
          persona?.id ||
          null;

        // El id correcto para PersonaProcesoItem es proceso_etapa_item_id.
        const itemId =
          item?.proceso_etapa_item_id ||
          item?.item_id ||
          item?.id ||
          null;

        // El proceso correcto debe salir primero de la etapa actual.
        const procesoId =
          etapa?.proceso_id ||
          personaProceso?.proceso_id ||
          item?.proceso_id ||
          null;

        return {
          persona_id: personaId,
          dni: persona?.dni || personaId || null,
          item_id: itemId,
          proceso_id: procesoId,
          proceso_etapa_id: etapa?.id || null,
          proceso_etapa_instancia_id:
            item?.proceso_etapa_instancia_id ||
            instancia?.id ||
            null,
          titulo_item:
            item?.descripcion ||
            item?.nombre ||
            "Evaluación médica",
        };
      })
      .filter(
        (evaluacion) =>
          evaluacion.persona_id && evaluacion.item_id
      );

    if (!evaluacionesPayload.length) {
      Swal.fire(
        "Error",
        "Las evaluaciones seleccionadas no tienen persona_id o item_id válidos.",
        "error"
      );
      return;
    }

    if (evaluacionesPayload.length !== seleccionadas.length) {
      const omitidas =
        seleccionadas.length - evaluacionesPayload.length;

      const continuar = await Swal.fire({
        title: "Hay evaluaciones incompletas",
        html: `
          <div style="text-align:left">
            <p>
              <b>${omitidas}</b> evaluación(es) no tienen identificadores
              válidos y serán omitidas.
            </p>
            <p>
              Se procesarán <b>${evaluacionesPayload.length}</b>
              evaluación(es).
            </p>
          </div>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Continuar",
        cancelButtonText: "Cancelar",
      });

      if (!continuar.isConfirmed) return;
    }

    const confirmado = await Swal.fire({
      title: "¿Confirmar calificación masiva?",
      html: `
        <div style="text-align:left">
          <p>
            Se calificarán <b>${evaluacionesPayload.length}</b>
            evaluación(es) como <b>${textoResultado}</b>.
          </p>
          ${
            formulario.resultado === "no_apto"
              ? `
                <p>
                  <b>Motivos de NO APTO:</b>
                  ${
                    formulario.motivos?.length
                      ? formulario.motivos.join(", ")
                      : "Sin motivos"
                  }
                </p>
              `
              : ""
          }

          ${
            formulario.observacion
              ? `
                <p>
                  <b>Observación:</b>
                  ${formulario.observacion}
                </p>
              `
              : ""
          }
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor:
        estado === "aprobado" ? "#16a34a" : "#dc2626",
    });

    if (!confirmado.isConfirmed) return;

    const usuarioNombre = Usuario
      ? `${Usuario.identificacion || ""} ${
          Usuario.apellido || ""
        }, ${Usuario.nombre || ""}`.trim()
      : "sistema";

    const payload = {
      evaluaciones: evaluacionesPayload,
      proceso_id: etapa?.proceso_id || null,
      estado,
      resultado: textoResultado,
      observacion: formulario.observacion,
      motivos:
        formulario.resultado === "no_apto"
          ? formulario.motivos || []
          : [],
      usuario_id:
        Usuario?.id ||
        Usuario?.usuarioID ||
        Usuario?.identificacion ||
        null,
      usuario_nombre: usuarioNombre,
    };

    const evaluacionesInvalidas = evaluacionesPayload.filter(
      (evaluacion) =>
        !evaluacion.persona_id ||
        !evaluacion.item_id ||
        !evaluacion.proceso_id
    );

    if (evaluacionesInvalidas.length > 0) {
      console.error(
        "Evaluaciones masivas con identificadores inválidos:",
        evaluacionesInvalidas
      );

      Swal.fire({
        title: "Identificadores inválidos",
        icon: "error",
        html: `
          <div style="text-align:left">
            <p>
              Hay <b>${evaluacionesInvalidas.length}</b> evaluación(es)
              sin persona_id, item_id o proceso_id válido.
            </p>
            <p style="font-size:12px">
              Revisá la consola para ver el detalle.
            </p>
          </div>
        `,
      });
      return;
    }

//     console.log(
//       "PAYLOAD TOGGLE PERSONA ITEM MASIVO:",
//       JSON.stringify(payload, null, 2)
//     );

    console.table(
      evaluacionesPayload.map((evaluacion) => ({
        dni: evaluacion.dni,
        persona_id: evaluacion.persona_id,
        proceso_id: evaluacion.proceso_id,
        proceso_etapa_id: evaluacion.proceso_etapa_id,
        instancia_id: evaluacion.proceso_etapa_instancia_id,
        item_id: evaluacion.item_id,
        titulo_item: evaluacion.titulo_item,
      }))
    );

    try {
      setGuardandoCalificacionMasiva(true);

      Swal.fire({
        title: "Guardando evaluaciones",
        html: `Procesando ${evaluacionesPayload.length} registro(s)...`,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      const response = await axios.post(
        `${API}/togglePersonaItemMasivo`,
        payload
      );

      const respuesta = response.data || {};
      const resumen = respuesta.resumen || {};

      const guardadas =
        Number(resumen.guardadas) ||
        respuesta.guardados?.length ||
        0;

      const cantidadErrores =
        Number(resumen.errores) ||
        respuesta.errores?.length ||
        0;

      // Primero refrescamos desde el backend y luego aplicamos la respuesta
      // del POST sobre el estado local. Así la pantalla se actualiza aunque
      // getPersonasPorEtapaPorRol todavía devuelva datos cacheados o atrasados.
      await fetchData();
      await fetchPresentes();
      aplicarGuardadosMasivosEnPantalla(respuesta.guardados || []);

      if (cantidadErrores === 0) {
        limpiarSeleccionEvaluaciones();

        await Swal.fire(
          "Calificación guardada",
          `${guardadas || evaluacionesPayload.length} evaluación(es) fueron calificadas como ${textoResultado}.`,
          "success"
        );

        return;
      }

      // Dejamos seleccionadas únicamente las evaluaciones que fallaron
      // para poder corregirlas o volver a intentar.
      const clavesFallidas = new Set(
        (respuesta.errores || []).map(
          (error) => `${error.persona_id}-${error.item_id}`
        )
      );

      setEvaluacionesSeleccionadas((prev) => {
        const siguiente = {};

        Object.entries(prev).forEach(([clave, registro]) => {
          const personaId =
            registro?.personaProceso?.persona?.id ||
            registro?.personaProceso?.persona_id ||
            registro?.personaProceso?.persona?.dni;

          const itemId =
            registro?.item?.proceso_etapa_item_id ||
            registro?.item?.item_id ||
            registro?.item?.id;

          if (clavesFallidas.has(`${personaId}-${itemId}`)) {
            siguiente[clave] = registro;
          }
        });

        return siguiente;
      });

      const erroresHtml = (respuesta.errores || [])
        .map((error) => {
          const identificacion =
            error.dni ||
            error.persona_id ||
            "Persona desconocida";

          return `
            <div style="margin-bottom:6px">
              <b>${identificacion}</b>
              ${error.item_id ? ` - Item ${error.item_id}` : ""}
              <br/>
              ${error.error || "Error desconocido"}
            </div>
          `;
        })
        .join("");

      await Swal.fire({
        title: "Proceso finalizado con observaciones",
        icon: "warning",
        html: `
          <div style="text-align:left">
            <p><b>Guardadas:</b> ${guardadas}</p>
            <p><b>Fallidas:</b> ${cantidadErrores}</p>

            <div
              style="
                max-height:250px;
                overflow:auto;
                font-size:12px;
                background:#f8fafc;
                padding:8px;
                border-radius:6px;
              "
            >
              ${erroresHtml || "El servidor no informó el detalle de los errores."}
            </div>

            <p style="margin-top:10px;font-size:12px">
              Las evaluaciones fallidas permanecen seleccionadas.
            </p>
          </div>
        `,
      });
    } catch (error) {
      console.error("Error calificación masiva:", error);

      const detalle =
        error?.response?.data?.detalle ||
        error?.response?.data?.error ||
        error?.response?.data?.mensaje ||
        error?.message ||
        "No se pudo realizar la calificación masiva.";

      Swal.fire(
        "Error",
        detalle,
        "error"
      );
    } finally {
      setGuardandoCalificacionMasiva(false);
    }
  };

  const iniciarCalificacionSeleccionadas = () => {
    if (!puedeCompletarSanidad) {
      sinPermiso();
      return;
    }

    const cola = evaluacionesVisibles.filter(
      ({ clave }) => evaluacionesSeleccionadas[clave]
    );

    if (!cola.length) {
      Swal.fire(
        "Sin evaluaciones",
        "Seleccioná al menos una evaluación para calificar.",
        "info"
      );
      return;
    }

    setColaEvaluaciones(cola);
    setIndiceEvaluacionCola(0);
    abrirEvaluacionDeCola(0, cola);
  };

  const avanzarEvaluacionSeleccionada = async () => {
    await fetchData();
    await fetchPresentes();

    const siguienteIndice = indiceEvaluacionCola + 1;

    setModalOpen(false);
    setItemSeleccionado(null);
    setTipoModal(null);

    if (siguienteIndice < colaEvaluaciones.length) {
      setTimeout(() => {
        abrirEvaluacionDeCola(siguienteIndice, colaEvaluaciones);
      }, 100);
      return;
    }

    Swal.fire(
      "Lote finalizado",
      "Ya recorriste todas las evaluaciones seleccionadas.",
      "success"
    );

    limpiarSeleccionEvaluaciones();
  };

  const cerrarModalEvaluacion = async () => {
    setModalOpen(false);
    setItemSeleccionado(null);
    setTipoModal(null);
    setColaEvaluaciones([]);
    setIndiceEvaluacionCola(-1);
    await fetchData();
    await fetchPresentes();
  };

  const exportToExcel = () => {
    const flat = [];

    // Evita que Excel interprete observaciones como fórmulas.
    const textoExcel = (valor) => {
      const texto = String(valor ?? "");
      return /^[\s]*[=+@-]/.test(texto) ? `'${texto}` : texto;
    };

    // Obtiene la última observación nueva registrada en el historial.
    const ultimaObservacion = (historial) => {
      const texto = String(historial || "");
      const bloques = texto.split(/---\s*ACTUALIZACI[ÓO]N\s*---/i);
      for (let i = bloques.length - 1; i >= 0; i--) {
        const coincidencia = bloques[i].match(
          /^Observación nueva:\s*(.*)$/im
        );
        if (coincidencia) return coincidencia[1].trim();
      }
      return "";
    };

    filteredData.forEach((p) => {
      p.instancias?.forEach((inst) => {
        inst.items?.forEach((item) => {
          const presentes = getPresentesPersona(p);
          const presenteSanidad = getPresenteSanidad(p);

          flat.push({
            DNI: p.persona.dni,
            Nombre: p.persona.nombre,
            Apellido: p.persona.apellido,
            TienePresente: presentes.length > 0 ? "SI" : "NO",
            CantidadPresentes: presentes.length,
            DependenciasPresentes: presentes
              .map(
                (presente) =>
                  presente?.dependencia || "Sin dependencia"
              )
              .join(" | "),
            UsuariosPresentes: presentes
              .map(
                (presente) =>
                  `${presente?.dependencia || "Sin dependencia"}: ${
                    presente?.usuario || ""
                  }`
              )
              .join(" | "),
            PresenteSanidad: presenteSanidad ? "SI" : "NO",
            FechaPresenteSanidad: formatearFechaPresente(
              presenteSanidad?.fecha
            ),
            HoraPresenteSanidad: formatearHoraPresente(
              presenteSanidad?.hora
            ),
            UsuarioPresenteSanidad:
              presenteSanidad?.usuario || "",
            Instancia: inst.nombre,
            Item: item.nombre || item.descripcion,
            Estado: item.estado,
            Resultado: item.resultado || "",
            Intervenido: item.intervenidoPorUsuario ? "SI" : "NO",
            UsuarioIntervencion: textoExcel(item.usuarioIntervencion || ""),
            FechaIntervencion: textoExcel(item.fechaIntervencion || ""),
            UltimaObservacion: textoExcel(
              ultimaObservacion(item.observaciones) ||
              (item.observacion && item.observacion !== "Carpeta Medica"
                ? item.observacion
                : "")
            ),
            HistorialObservaciones: textoExcel(item.observaciones || ""),
          });
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(flat);
    ws["!cols"] = Object.keys(flat[0] || {}).map((columna) => ({
      wch: columna === "HistorialObservaciones" ? 75 :
        columna === "UltimaObservacion" ? 55 :
        columna === "UsuarioIntervencion" ? 32 : 20,
    }));
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Sanidad");

    const buffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(new Blob([buffer]), `sanidad_${etapa.nombre}.xlsx`);
  };

  if (loading) return <p>Cargando...</p>;

  return (
    <div
      style={{
        background: "#fff",
        padding: 15,
        marginTop: 10,
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
      }}
    >
      <h4>🩺 {etapa.nombre} - Sanidad</h4>

      {esPerfeccionamiento && (
        <div style={styles.avisoFiltro}>
          Perfeccionamiento: por defecto se muestran personas con al menos una documentación entregada o aprobada. Podés incluir también quienes todavía no entregaron documentación.
        </div>
      )}

     

      <div style={styles.resumenContainer}>
        <div style={styles.cardMatriculados}>
          <b>Matriculados etapa</b>

          <div style={styles.numeroMatriculados}>
            {cantidadMatriculadosEtapa}
          </div>

          <div style={styles.resumenTexto}>
            Filtrados: {filteredData.length}
          </div>

          {!esPerfeccionamiento && (
            <div style={styles.resumenPresentesGeneral}>
              <div>
                ✔ Con algún presente:{" "}
                <b>{resumenPresentes.totalPersonasConPresente}</b>
              </div>

              <div>
                🧾 Registros de presente:{" "}
                <b>{resumenPresentes.totalRegistros}</b>
              </div>
            </div>
          )}
        </div>

        {Object.values(resumenPorInstancia).map((r, i) => (
          <div key={i} style={styles.cardResumen}>
            <b>{r.nombre}</b>

            <div style={styles.resumenTexto}>✔ Completados {r.aprobado}</div>
            {!esPerfeccionamiento && (
              <>
                <div style={styles.resumenTexto}>● En proceso {r.en_progreso}</div>
                <div style={styles.resumenTexto}>✖ Pendientes {r.pendiente}</div>
              </>
            )}
          </div>
        ))}
      </div>

      {!esPerfeccionamiento && (
        <div style={styles.asistenciaPorFechaContainer}>
          <div style={styles.asistenciaPorFechaHeader}>
            <div>
              <b>📅 Presentes por fecha</b>
              <div style={styles.asistenciaAyuda}>
                Tocá Presentes para ver solamente quienes dieron presente
                ese día, o Ausentes para ver quienes todavía no lo dieron.
              </div>
            </div>

            <div style={styles.asistenciaFechaManual}>
              <input
                type="date"
                value={filtroPresenteFecha || ""}
                onChange={(e) => {
                  const fecha = e.target.value;

                  if (!fecha) {
                    setFiltroPresenteFecha(null);
                    setFiltroTipoAsistencia("todos");
                    return;
                  }

                  setFiltroPresenteFecha(fecha);
                  setFiltroTipoAsistencia("presentes");
                  setPaginaActual(1);
                }}
                style={styles.inputFechaAsistencia}
              />

              {filtroPresenteFecha && (
                <button
                  type="button"
                  onClick={() => {
                    setFiltroPresenteFecha(null);
                    setFiltroTipoAsistencia("todos");
                    setPaginaActual(1);
                  }}
                  style={styles.btnLimpiarAsistencia}
                >
                  ✖ Limpiar
                </button>
              )}
            </div>
          </div>

          <div style={styles.fechasAsistenciaGrid}>
            {Object.entries(resumenPresentes.porFecha)
              .sort(([fechaA], [fechaB]) =>
                fechaB.localeCompare(fechaA)
              )
              .map(([fecha, cantidadPresentes]) => {
                const cantidadAusentes = Math.max(
                  cantidadMatriculadosEtapa -
                    cantidadPresentes,
                  0
                );

                const fechaActiva =
                  filtroPresenteFecha === fecha;

                const presentesActivo =
                  fechaActiva &&
                  filtroTipoAsistencia === "presentes";

                const ausentesActivo =
                  fechaActiva &&
                  filtroTipoAsistencia === "restantes";

                return (
                  <div
                    key={fecha}
                    style={{
                      ...styles.cardAsistenciaFecha,
                      borderColor: fechaActiva
                        ? "#2563eb"
                        : "#bbf7d0",
                    }}
                  >
                    <div style={styles.fechaAsistenciaTitulo}>
                      📅 {formatearFechaPresente(fecha)}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setFiltroPresenteFecha(fecha);
                        setFiltroTipoAsistencia("presentes");
                        setFiltroPresentes("todos");
                        setPaginaActual(1);
                        setEvaluacionesSeleccionadas({});
                      }}
                      style={{
                        ...styles.btnFechaPresentes,
                        ...(presentesActivo
                          ? styles.btnFechaPresentesActivo
                          : {}),
                      }}
                    >
                      ✔ Presentes{" "}
                      <b>{cantidadPresentes}</b>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setFiltroPresenteFecha(fecha);
                        setFiltroTipoAsistencia("restantes");
                        setFiltroPresentes("todos");
                        setPaginaActual(1);
                        setEvaluacionesSeleccionadas({});
                      }}
                      style={{
                        ...styles.btnFechaAusentes,
                        ...(ausentesActivo
                          ? styles.btnFechaAusentesActivo
                          : {}),
                      }}
                    >
                      ⏳ Ausentes{" "}
                      <b>{cantidadAusentes}</b>
                    </button>

                    <div style={styles.totalFechaAsistencia}>
                      👥 Total{" "}
                      <b>{cantidadMatriculadosEtapa}</b>
                    </div>
                  </div>
                );
              })}

            {Object.keys(resumenPresentes.porFecha).length === 0 && (
              <div style={styles.sinFechasAsistencia}>
                Todavía no hay registros de presentes para esta etapa.
              </div>
            )}
          </div>

          {filtroPresenteFecha && (
            <div style={styles.filtroFechaActivo}>
              Mostrando:{" "}
              <b>
                {filtroTipoAsistencia === "restantes"
                  ? "Ausentes"
                  : "Presentes"}
              </b>{" "}
              del{" "}
              <b>
                {formatearFechaPresente(
                  filtroPresenteFecha
                )}
              </b>
              {" · "}
              <b>{filteredData.length}</b> persona(s)
            </div>
          )}
        </div>
      )}

      <div style={styles.paginador}>
          <button
            onClick={() => setPaginaActual((p) => Math.max(p - 1, 1))}
            disabled={paginaActual === 1}
          >
            ⬅ Anterior
          </button>

          <span>
            Página {paginaActual} de {totalPaginas}
          </span>

          <button
            onClick={() =>
              setPaginaActual((p) => Math.min(p + 1, totalPaginas))
            }
            disabled={paginaActual === totalPaginas}
          >
            Siguiente ➡
          </button>
        </div>

      <div style={styles.buscadorContainer}>
        <input
          placeholder="Buscar persona"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPaginaActual(1);
          }}
          style={styles.inputBuscar}
        />

        <select
          value={filtroPresentes}
          onChange={(e) => {
            setFiltroPresentes(e.target.value);
            setPaginaActual(1);
            setEvaluacionesSeleccionadas({});
          }}
          style={styles.selectOrdenAlfabetico}
          title="Filtrar por presentes"
        >
          <option value="todos">Todos los inscriptos</option>
          <option value="presentes">Sólo con algún presente</option>
          <option value="sin_presente">Sólo sin presente</option>
          <option value="sanidad">Sólo con presente de Sanidad</option>
        </select>

        <select
          value={ordenAlfabetico}
          onChange={(e) => setOrdenAlfabetico(e.target.value)}
          style={styles.selectOrdenAlfabetico}
          title="Ordenar filas"
        >
          <option value="apellido_asc">Apellido A → Z</option>
          <option value="apellido_desc">Apellido Z → A</option>
          <option value="nombre_asc">Nombre A → Z</option>
          <option value="nombre_desc">Nombre Z → A</option>
        </select>

        <button onClick={exportToExcel}>Excel</button>
      </div>


      <div style={styles.barraEvaluaciones}>
        {esPerfeccionamiento && (
          <label style={styles.checkLabel}>
            <input
              type="checkbox"
              checked={mostrarPersonasSinDocumentacion}
              onChange={(e) => {
                setMostrarPersonasSinDocumentacion(e.target.checked);
                setPaginaActual(1);
                setEvaluacionesSeleccionadas({});
              }}
            />
            Mostrar también personas sin documentación entregada
          </label>
        )}

        <label style={styles.checkLabel}>
          <input
            type="checkbox"
            checked={ocultarEvaluacionesCorregidas}
            onChange={(e) => {
              setOcultarEvaluacionesCorregidas(e.target.checked);
              setEvaluacionesSeleccionadas({});
            }}
          />
          Ocultar filas con evaluaciones ya realizadas
        </label>

        <label style={styles.checkLabel}>
          <input
            type="checkbox"
            checked={mostrarSoloPendientes}
            onChange={(e) => {
              setMostrarSoloPendientes(e.target.checked);
              setPaginaActual(1);
              setEvaluacionesSeleccionadas({});
            }}
          />
          Mostrar solo quienes tienen pendientes
        </label>


        <label style={styles.checkLabel}>
          <input
            type="checkbox"
            checked={soloIncompletosSinIntervenir}
            onChange={(e) => {
              setSoloIncompletosSinIntervenir(e.target.checked);
              setPaginaActual(1);
              setEvaluacionesSeleccionadas({});
            }}
          />
          Incompletos sin intervenir
        </label>

        <button
          type="button"
          onClick={toggleSeleccionarTodasEvaluaciones}
          disabled={!evaluacionesVisibles.length}
          style={styles.btnAccion}
        >
          {todasLasEvaluacionesVisiblesSeleccionadas
            ? "Quitar selección visible"
            : "Seleccionar todas las evaluaciones"}
        </button>

        <button
          type="button"
          onClick={calificarSeleccionadasMasivamente}
          disabled={!cantidadSeleccionadas || guardandoCalificacionMasiva}
          style={{
            ...styles.btnCalificarMasivo,
            opacity:
              cantidadSeleccionadas && !guardandoCalificacionMasiva
                ? 1
                : 0.55,
          }}
        >
          {guardandoCalificacionMasiva
            ? "Guardando..."
            : `Apto / No apto masivo (${cantidadSeleccionadas})`}
        </button>

        <button
          type="button"
          onClick={iniciarCalificacionSeleccionadas}
          disabled={!cantidadSeleccionadas || guardandoCalificacionMasiva}
          style={{
            ...styles.btnCalificar,
            opacity:
              cantidadSeleccionadas && !guardandoCalificacionMasiva
                ? 1
                : 0.55,
          }}
        >
          Revisar una por una ({cantidadSeleccionadas})
        </button>

        {cantidadSeleccionadas > 0 && (
          <button
            type="button"
            onClick={limpiarSeleccionEvaluaciones}
            style={styles.btnSecundario}
          >
            Limpiar selección
          </button>
        )}

        <span style={styles.contadorEvaluaciones}>
          Evaluaciones visibles: {evaluacionesVisibles.length}
        </span>
      </div>

      {!isMobile && (
        <table style={styles.tabla}>
          <thead>
            <tr>
              <th style={styles.th}>#</th>
              <th style={styles.th}>DNI</th>
              <th style={styles.th}>Apellido</th>
              <th style={styles.th}>Nombre</th>
              <th
                style={{
                  ...styles.th,
                  ...styles.thPresentesClickeable,
                  ...(filtroPresentes === "presentes"
                    ? styles.thPresentesActivo
                    : {}),
                }}
                onClick={() => {
                  setFiltroPresentes(
                    filtroPresentes === "presentes"
                      ? "todos"
                      : "presentes"
                  );
                  setPaginaActual(1);
                  setEvaluacionesSeleccionadas({});
                }}
                title="Tocar para mostrar sólo personas con presente"
              >
                {filtroPresentes === "presentes"
                  ? "✔ Presentes — FILTRANDO"
                  : "Presentes"}
              </th>

              {dataPaginada[0]?.instancias?.map((inst) => (
                <th key={inst.id} style={styles.th}>
                  {inst.nombre}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {dataPaginada.map((p, index) => (
              <tr
                key={p.persona.dni}
                style={{
                  background: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                }}
              >
                <td style={styles.td}>
                  {(paginaActual - 1) * filasPorPagina + index + 1}
                </td>

                <td style={styles.td}>{p.persona.dni}</td>
                <td style={styles.td}>{p.persona.apellido}</td>
                <td style={styles.td}>{p.persona.nombre}</td>

                <td style={styles.td}>
                  {getPresentesPersona(p).length > 0 ? (
                    <div style={styles.presentesContainer}>
                      {getPresentesPersona(p).map(
                        (presente, indexPresente) => (
                          <button
                            key={
                              presente?.id ||
                              `${presente?.dependencia}-${presente?.fecha}-${presente?.hora}-${indexPresente}`
                            }
                            type="button"
                            onClick={() =>
                              mostrarPresentes(
                                getPresentesPersona(p)
                              )
                            }
                            style={{
                              ...styles.presenteDependenciaChip,
                              background:
                                getColorDependencia(
                                  presente?.dependencia
                                ),
                              color:
                                getColorTextoDependencia(
                                  presente?.dependencia
                                ),
                            }}
                            title={`Presente dado por ${
                              presente?.dependencia ||
                              "Sin dependencia"
                            } - Usuario: ${
                              presente?.usuario || "—"
                            }`}
                          >
                            <b>
                              ✔{" "}
                              {presente?.dependencia ||
                                "Sin dependencia"}
                            </b>

                            <span>
                              {formatearFechaPresente(
                                presente?.fecha
                              )}{" "}
                              {formatearHoraPresente(
                                presente?.hora
                              )}
                            </span>

                            <span style={styles.usuarioPresente}>
                              {presente?.usuario || ""}
                            </span>
                          </button>
                        )
                      )}
                    </div>
                  ) : (
                    <span style={styles.ausenteSanidadChip}>
                      —
                    </span>
                  )}
                </td>

                {p.instancias?.map((inst) => (
                  <td key={inst.id} style={styles.td}>
                    <div style={styles.itemsContainer}>
                      {inst.items?.map((item, i) => {
                        const clave = getClaveEvaluacion(p, inst, item);
                        const seleccionado =
                          Boolean(evaluacionesSeleccionadas[clave]);

                        return (
                          <div
                            key={item.persona_proceso_item_id || item.id}
                            style={styles.itemConCheck}
                          >
                            {esEvaluacion(item) && (
                              <input
                                type="checkbox"
                                checked={seleccionado}
                                onChange={() =>
                                  toggleEvaluacionSeleccionada(p, inst, item)
                                }
                                onClick={(e) => e.stopPropagation()}
                                title="Seleccionar evaluación"
                              />
                            )}

                            <span
                              onClick={() => handleClickItem(p, inst, item)}
                              style={{
                                ...styles.itemChip,
                                cursor: puedeCompletarSanidad
                                  ? "pointer"
                                  : "not-allowed",
                                opacity: puedeCompletarSanidad ? 1 : 0.6,
                                background: getColor(item),
                                outline: seleccionado
                                  ? "2px solid #2563eb"
                                  : "none",
                              }}
                            >
                              {item.descripcion ||
                                item.nombre ||
                                `Item ${i + 1}`}{" "}
                              {getIcon(item)}
                              {indicadorIntervencion(item)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isMobile && (
        <div style={{ marginTop: 10 }}>
          {dataPaginada.map((p, index) => (
            <div
              key={p.persona.dni}
              style={{
                ...styles.cardMobile,
                background: index % 2 === 0 ? "#fff" : "#f8fafc",
              }}
            >
              <b>
                #{(paginaActual - 1) * filasPorPagina + index + 1} -{" "}
                {p.persona.apellido}, {p.persona.nombre}
              </b>

              <div>DNI: {p.persona.dni}</div>

              <div style={{ marginTop: 6 }}>
                {getPresentesPersona(p).length > 0 ? (
                  <div style={styles.presentesMobileContainer}>
                    {getPresentesPersona(p).map(
                      (presente, indexPresente) => (
                        <div
                          key={
                            presente?.id ||
                            `${presente?.dependencia}-${presente?.fecha}-${presente?.hora}-${indexPresente}`
                          }
                          onClick={() =>
                            mostrarPresentes(
                              getPresentesPersona(p)
                            )
                          }
                          style={{
                            ...styles.presenteDependenciaMobile,
                            background:
                              getColorDependencia(
                                presente?.dependencia
                              ),
                            color:
                              getColorTextoDependencia(
                                presente?.dependencia
                              ),
                          }}
                        >
                          <b>
                            ✔{" "}
                            {presente?.dependencia ||
                              "Sin dependencia"}
                          </b>

                          <span>
                            {formatearFechaPresente(
                              presente?.fecha
                            )}{" "}
                            {formatearHoraPresente(
                              presente?.hora
                            )}
                          </span>

                          {presente?.usuario && (
                            <small>
                              {presente.usuario}
                            </small>
                          )}
                        </div>
                      )
                    )}
                  </div>
                ) : (
                  <div style={styles.ausenteSanidadMobile}>
                    Sin presentes
                  </div>
                )}
              </div>

              {p.instancias?.map((inst) => (
                <div key={inst.id} style={{ marginTop: 8 }}>
                  <b>{inst.nombre}</b>

                  <div style={styles.itemsContainer}>
                    {inst.items?.map((item, i) => {
                      const clave = getClaveEvaluacion(p, inst, item);
                      const seleccionado =
                        Boolean(evaluacionesSeleccionadas[clave]);

                      return (
                        <div
                          key={item.persona_proceso_item_id || item.id}
                          style={styles.itemConCheckMobile}
                        >
                          {esEvaluacion(item) && (
                            <input
                              type="checkbox"
                              checked={seleccionado}
                              onChange={() =>
                                toggleEvaluacionSeleccionada(p, inst, item)
                              }
                              onClick={(e) => e.stopPropagation()}
                              title="Seleccionar evaluación"
                            />
                          )}

                          <span
                            onClick={() => handleClickItem(p, inst, item)}
                            style={{
                              ...styles.itemChipMobile,
                              cursor: puedeCompletarSanidad
                                ? "pointer"
                                : "not-allowed",
                              opacity: puedeCompletarSanidad ? 1 : 0.6,
                              background: getColor(item),
                              outline: seleccionado
                                ? "2px solid #2563eb"
                                : "none",
                            }}
                          >
                            {item.descripcion ||
                              item.nombre ||
                              `Item ${i + 1}`}{" "}
                            {getIcon(item)}
                            {indicadorIntervencion(item)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {filteredData.length === 0 && <p>No hay inscriptos</p>}

      {modalOpen && itemSeleccionado && (
        <div style={styles.modalFondo}>
          <div
            style={{
              ...styles.modalContenido,
              ...(tipoModal === "carpeta_medica" || tipoModal === "archivo"
                ? { width: "min(1120px, 96vw)", maxWidth: "96vw", height: "auto", maxHeight: "94vh" }
                : {}),
            }}
          >
            <div style={styles.modalAcciones}>
              {colaEvaluaciones.length > 0 && indiceEvaluacionCola >= 0 && (
                <>
                  <span style={styles.progresoLote}>
                    Evaluación {indiceEvaluacionCola + 1} de{" "}
                    {colaEvaluaciones.length}
                  </span>

                  <button
                    type="button"
                    onClick={avanzarEvaluacionSeleccionada}
                    style={styles.btnSiguienteEvaluacion}
                  >
                    {indiceEvaluacionCola + 1 < colaEvaluaciones.length
                      ? "Siguiente evaluación ➡"
                      : "Finalizar lote ✔"}
                  </button>
                </>
              )}

              <button
                type="button"
                onClick={cerrarModalEvaluacion}
                style={styles.btnCerrar}
              >
                ❌
              </button>
            </div>

            {tipoModal === "evaluacion_medica" && (
              <EvaluacionMedicaItem
                datos={itemSeleccionado}
                etapa={etapa}
                refres={fetchData}
              />
            )}

            {tipoModal === "archivo_solo_lectura" && (
              <UploadArchivosItem
                datos={itemSeleccionado}
                etapa={etapa}
                refres={fetchData}
                soloLectura={true}
                puedeEliminar={false}
                puedeCargar={false}
              />
            )}
            {tipoModal === "completoPerfeccionamiento" && (
              <ModuloCompletoPerfeccionamiento
                datos={itemSeleccionado}
                etapa={etapa}
                refres={fetchData}
                soloLectura={false}
                puedeEliminar={false}
                puedeCargar={false}
              />
            )}

            {(tipoModal === "archivo" || tipoModal === "carpeta_medica") && (
              <CarpetaMedicaItem
                datos={itemSeleccionado}
                etapa={etapa}
                refres={fetchData}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  avisoFiltro: {
    marginTop: 8,
    marginBottom: 8,
    padding: 8,
    borderRadius: 6,
    background: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
    fontSize: 13,
    fontWeight: "bold",
  },
  resumenPresentesGeneral: {
    marginTop: 8,
    display: "flex",
    flexDirection: "column",
    gap: 4,
    fontSize: 13,
    color: "#166534",
  },

  asistenciaPorFechaContainer: {
    marginTop: 14,
    padding: 12,
    border: "1px solid #bbf7d0",
    borderRadius: 10,
    background: "#f0fdf4",
  },

  asistenciaPorFechaHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 10,
  },

  asistenciaAyuda: {
    marginTop: 3,
    fontSize: 12,
    color: "#475569",
  },

  asistenciaFechaManual: {
    display: "flex",
    alignItems: "center",
    gap: 7,
  },

  inputFechaAsistencia: {
    padding: "7px 9px",
    borderRadius: 7,
    border: "1px solid #86efac",
    background: "#fff",
  },

  btnLimpiarAsistencia: {
    padding: "7px 9px",
    borderRadius: 7,
    border: "1px solid #cbd5e1",
    background: "#fff",
    cursor: "pointer",
  },

  fechasAsistenciaGrid: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },

  cardAsistenciaFecha: {
    minWidth: 205,
    padding: 10,
    border: "2px solid #bbf7d0",
    borderRadius: 9,
    background: "#ffffff",
  },

  fechaAsistenciaTitulo: {
    fontWeight: "bold",
    marginBottom: 8,
    color: "#0f172a",
  },

  btnFechaPresentes: {
    width: "100%",
    marginBottom: 6,
    padding: "7px 9px",
    borderRadius: 7,
    border: "1px solid #86efac",
    background: "#dcfce7",
    color: "#166534",
    cursor: "pointer",
    textAlign: "left",
  },

  btnFechaPresentesActivo: {
    background: "#16a34a",
    color: "#ffffff",
    border: "1px solid #15803d",
    outline: "2px solid #15803d",
  },

  btnFechaAusentes: {
    width: "100%",
    marginBottom: 6,
    padding: "7px 9px",
    borderRadius: 7,
    border: "1px solid #fcd34d",
    background: "#fef3c7",
    color: "#92400e",
    cursor: "pointer",
    textAlign: "left",
  },

  btnFechaAusentesActivo: {
    background: "#f59e0b",
    color: "#ffffff",
    border: "1px solid #d97706",
    outline: "2px solid #d97706",
  },

  totalFechaAsistencia: {
    fontSize: 12,
    color: "#475569",
    marginTop: 3,
  },

  filtroFechaActivo: {
    marginTop: 10,
    padding: "8px 10px",
    borderRadius: 7,
    background: "#dbeafe",
    border: "1px solid #93c5fd",
    color: "#1e3a8a",
    fontSize: 13,
  },

  sinFechasAsistencia: {
    padding: 10,
    color: "#64748b",
    fontSize: 13,
  },

  resumenContainer: {
    display: "flex",
    gap: 15,
    flexWrap: "wrap",
    marginTop: 10,
  },
  cardMatriculados: {
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 10,
    minWidth: 180,
    background: "#eff6ff",
  },
  numeroMatriculados: {
    fontSize: 22,
    marginTop: 5,
    fontWeight: "bold",
  },
  cardResumen: {
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 10,
    minWidth: 180,
    background: "#fafafa",
  },
  resumenTexto: {
    fontSize: 13,
    marginTop: 5,
  },

  btnResumenPresentes: {
    display: "block",
    marginTop: 6,
    padding: "5px 8px",
    borderRadius: 7,
    border: "1px solid #86efac",
    background: "#dcfce7",
    color: "#166534",
    fontWeight: "bold",
    cursor: "pointer",
    textAlign: "left",
  },

  btnResumenPresentesActivo: {
    background: "#16a34a",
    color: "#ffffff",
    border: "1px solid #15803d",
  },

  thPresentesClickeable: {
    cursor: "pointer",
    userSelect: "none",
  },

  thPresentesActivo: {
    background: "#16a34a",
    color: "#ffffff",
  },

  avisoFiltroPresentesActivo: {
    marginTop: 10,
    padding: "8px 10px",
    borderRadius: 8,
    background: "#dcfce7",
    border: "1px solid #86efac",
    color: "#166534",
    fontSize: 13,
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },

  btnQuitarFiltroPresentes: {
    padding: "4px 8px",
    borderRadius: 6,
    border: "1px solid #16a34a",
    background: "#ffffff",
    color: "#166534",
    fontWeight: "bold",
    cursor: "pointer",
  },
  paginador: {
    marginTop: 15,
    display: "flex",
    gap: 10,
    alignItems: "center",
  },
  avisoPaginaUnica: {
    marginTop: 12,
    padding: "8px 10px",
    borderRadius: 7,
    background: "#eff6ff",
    border: "1px solid #93c5fd",
    color: "#1e3a8a",
    fontSize: 13,
    fontWeight: "bold",
  },
  buscadorContainer: {
    display: "flex",
    gap: 10,
    marginTop: 10,
  },
  barraEvaluaciones: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 10,
    padding: 10,
    border: "1px solid #bfdbfe",
    borderRadius: 8,
    background: "#eff6ff",
  },
  checkLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontWeight: "bold",
    fontSize: 13,
    cursor: "pointer",
  },
  btnAccion: {
    padding: "7px 10px",
    borderRadius: 7,
    border: "1px solid #94a3b8",
    background: "#fff",
    cursor: "pointer",
  },
  btnCalificarMasivo: {
    padding: "7px 12px",
    borderRadius: 7,
    border: "1px solid #1d4ed8",
    background: "#2563eb",
    color: "#fff",
    fontWeight: "bold",
    cursor: "pointer",
  },
  btnCalificar: {
    padding: "7px 12px",
    borderRadius: 7,
    border: "1px solid #166534",
    background: "#16a34a",
    color: "#fff",
    fontWeight: "bold",
    cursor: "pointer",
  },
  btnSecundario: {
    padding: "7px 10px",
    borderRadius: 7,
    border: "1px solid #cbd5e1",
    background: "#f8fafc",
    cursor: "pointer",
  },
  contadorEvaluaciones: {
    fontSize: 12,
    color: "#475569",
    marginLeft: "auto",
  },
  inputBuscar: {
    flex: 1,
    padding: 8,
    borderRadius: 8,
    border: "1px solid #ccc",
  },
  selectOrdenAlfabetico: {
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #94a3b8",
    background: "#fff",
    minWidth: 170,
    cursor: "pointer",
  },
  presentesContainer: {
    display: "flex",
    gap: 5,
    flexWrap: "wrap",
    minWidth: 220,
    maxWidth: 420,
  },
  presenteDependenciaChip: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 2,
    border: "none",
    borderRadius: 7,
    padding: "6px 9px",
    cursor: "pointer",
    fontSize: 10,
    minWidth: 105,
    boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
  },
  usuarioPresente: {
    fontSize: 9,
    opacity: 0.9,
    maxWidth: 140,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  presentesMobileContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },
  presenteDependenciaMobile: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: "7px 9px",
    borderRadius: 7,
    cursor: "pointer",
    fontSize: 12,
  },
  presenteSanidadChip: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    border: "none",
    borderRadius: 7,
    background: "#22c55e",
    color: "#fff",
    padding: "6px 9px",
    cursor: "pointer",
    fontSize: 11,
    minWidth: 92,
  },

  ausenteSanidadChip: {
    color: "#94a3b8",
    fontSize: 16,
  },

  presenteSanidadMobile: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    padding: "7px 9px",
    borderRadius: 7,
    background: "#dcfce7",
    border: "1px solid #86efac",
    color: "#166534",
    cursor: "pointer",
    fontSize: 12,
  },

  ausenteSanidadMobile: {
    padding: "6px 8px",
    borderRadius: 7,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#94a3b8",
    fontSize: 12,
  },

  tabla: {
    width: "100%",
    minWidth: 1300,
    marginTop: 10,
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    border: "1px solid #ddd",
    padding: "8px 6px",
    background: "#e5e7eb",
    position: "sticky",
    top: 0,
    zIndex: 2,
    whiteSpace: "nowrap",
  },
  td: {
    border: "1px solid #e5e7eb",
    padding: "7px 6px",
    verticalAlign: "top",
    whiteSpace: "nowrap",
  },
  itemsContainer: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  itemConCheck: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  itemConCheckMobile: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    width: "100%",
  },
  itemChip: {
    padding: "2px 6px",
    borderRadius: 4,
    display: "inline-block",
    fontSize: 12,
  },
  itemChipMobile: {
    padding: "4px 6px",
    borderRadius: 6,
    display: "inline-block",
    fontSize: 12,
  },
  cardMobile: {
    border: "1px solid #ddd",
    borderRadius: 8,
    marginBottom: 10,
    padding: 10,
  },
  modalFondo: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  modalContenido: {
    background: "#fff",
    padding: 20,
    borderRadius: 10,
    width: "95vw",
    height: "95vh",
    maxWidth: "95vw",
    maxHeight: "95vh",
    boxSizing: "border-box",
    overflow: "auto",
  },
  modalAcciones: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    paddingBottom: 10,
    background: "#fff",
  },
  progresoLote: {
    fontWeight: "bold",
    color: "#1e3a8a",
  },
  btnSiguienteEvaluacion: {
    padding: "8px 12px",
    borderRadius: 7,
    border: "1px solid #1d4ed8",
    background: "#2563eb",
    color: "#fff",
    fontWeight: "bold",
    cursor: "pointer",
  },
  btnCerrar: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: 18,
  },
};

export default ListadoInscriptoSanidad; 